import { BreadCrumb } from 'primereact/breadcrumb';
import { DataTable, DataTableExpandedRows, DataTableStateEvent } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { Column } from 'primereact/column';
import { FC, Suspense, useEffect, useRef } from 'react';
import { QueryComponent } from './QueryComponent';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { PageInfo } from '../../models/PageInfo';
import { SkillRowContent } from './SkillRowContent';
import { useDebounce } from 'primereact/hooks';
import { Button } from 'primereact/button';
import { AssignmentSidebarOpenModal } from '../AssignmentSidebar/AssignemntSidebarOpenModal/AssignmentSidebarOpenModal';
import { Toast } from 'primereact/toast';
import { removeHighlight, skillUrl } from '../../utils/removeSearchHighlight';
import { SelectAssignmentModeModal } from '../../models/Assignment';
import { AssignmentOpenModalButton } from '../Assignments/AssignmentOpenModalButton';
import { StandardState } from '../../models/Standard';
import { PresentLessonButton } from '../TeacherLedLesson/PresentLessonButton';

//Commented until we implement assign button and sidebar
/*import { useLazyLoadQuery } from 'react-relay';
import { hasStudentsRosteredQuery } from '../../graphQLOperations/Dashboard';
import { DashboardStudentsRosterQuery } from '../../graphQLOperations/__generated__/DashboardStudentsRosterQuery.graphql';*/

export interface SkillsTableFilterState {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  sortField: string;
  sortOrder: 1 | -1;
  totalPages: number;
  filters: Filters;
  multiSortMeta: null;
  prevPage: number;
}

interface Filters {
  searchQuery: string;
  [key: string]: string | { id: string; name: string }[] | null;
}

const filterStateInitValue: SkillsTableFilterState = {
  first: 0,
  rows: 5,
  page: 0,
  pageCount: 0,
  sortField: 'name',
  sortOrder: 1,
  totalPages: 1,
  filters: {
    searchQuery: '',
  },
  multiSortMeta: null,
  prevPage: 0,
};

export interface SkillTableItem {
  id: string;
  targetSkills?: TargetSkill[] | null;
  name: string;
  summary: string;
  code: string;
  standards: StandardState[];
  skillAncestries: Ancestor[];
  assessmentInfo: string;
  lessons?: { id: string }[] | null;
}

export interface Ancestor {
  skillRelevance: { id: string };
  ancestorSkill: { id: string; name: string; code: string };
}

export interface TargetSkill {
  target: Target;
}

export interface Target {
  id: string;
  name: string;
  summary: string;
  targetGroup: TargetGroup;
}

export interface TargetGroup {
  name: string;
  id: string;
  standardGrade?: { id: string; name: string } | null;
}

export enum AncestriesRelevanceIds {
  HELPFUL = 'SkillRelevanceNode:2',
  ESSENTIAL = 'SkillRelevanceNode:3',
}

const SkillsTable: FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [skillList, setSkillList] = useState<SkillTableItem[] | null>(null);
  const breadcrumbItems = [{ label: 'Plan', url: '/planning' }, { label: 'Skills' }];
  const [filterState, setFilterState] = useState<SkillsTableFilterState>(filterStateInitValue);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [searchTerm, debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce('', 500);
  const [showCreateAssignmentModel, setCreateAssignmentModal] = useState<SelectAssignmentModeModal>({
    visible: false,
    skill: null,
  });

  useEffect(() => {
    if (!firstLoad) {
      setFetchingData(true);
      setFilterState({
        ...filterStateInitValue,
        rows: filterState.rows,
        filters: { ...filterState.filters, searchQuery: debouncedSearchTerm },
      });
    }
  }, [debouncedSearchTerm]);

  const representativesItemTemplate = (element: { name: string; description: string; code: string }) => {
    return (
      <>
        <h4 key={element.code} dangerouslySetInnerHTML={{ __html: `${element.name} (${element.code})` }}></h4>
      </>
    );
  };
  const isRowExpanded = (rowData: SkillTableItem) => {
    return !!(expandedRows && expandedRows[rowData.id]);
  };
  const toggleRowExpansion = (rowData: SkillTableItem) => {
    setExpandedRows(prev => {
      const current = (prev || {}) as DataTableExpandedRows;
      const next: DataTableExpandedRows = { ...current };

      if (next[rowData.id]) {
        delete next[rowData.id];
      } else {
        next[rowData.id] = true;
      }
      return next;
    });
  };

  const firstLessonSkillId = skillList?.find(s => s.lessons?.length)?.id;

  return (
    <>
      <AssignmentSidebarOpenModal
        showCreateAssignmentModel={showCreateAssignmentModel}
        setCreateAssignmentModal={setCreateAssignmentModal}
      />

      <div className="grid nc_content-frame nc_page-inner pb-8">
        <Toolbar className="col-10 col-offset-1 nc_page-header" center={<h1>Skills</h1>} />
        <BreadCrumb
          className="col-10 col-offset-1 nc_breadcrumb"
          model={breadcrumbItems}
          home={{ icon: 'nc_icon-nc_home icon-sm', url: '/' }}
        />
        {skillList && (
          <div className="col-10 col-offset-1 grid nc_block">
            <Toast ref={toast} position="bottom-center" />
            <DataTable
              header={
                <InputText
                  placeholder="Search for skill"
                  className="w-full"
                  value={searchTerm}
                  onChange={e => {
                    setDebouncedSearchTerm(e.target.value);
                  }}
                />
              }
              lazy
              value={skillList}
              loading={fetchingData}
              stripedRows
              paginator
              dataKey="id"
              rows={filterState.rows}
              rowsPerPageOptions={[5, 10, 25]}
              sortField="name"
              sortOrder={filterState.sortOrder}
              totalRecords={totalRecords}
              filterDisplay="row"
              emptyMessage="No skills found."
              scrollable
              first={filterState.first}
              scrollHeight="600px"
              className="w-full"
              expandedRows={expandedRows}
              rowExpansionTemplate={data => (
                <div id={`skill-details-${data.id}`}>
                  <SkillRowContent skill={data} />
                </div>
              )}
              onPage={(e: DataTableStateEvent) => {
                setFetchingData(true);
                const newPage: SkillsTableFilterState = {
                  ...filterState,
                  page: e.page,
                  rows: e.rows,
                  first: e.first,
                  pageCount: e.pageCount!,
                  prevPage: filterState.page!,
                };
                setFilterState(newPage);
              }}
              onSort={() => {
                setFetchingData(true);
                setFilterState({ ...filterState, sortOrder: filterState.sortOrder === -1 ? 1 : -1 });
              }}
              paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            >
              <Column
                style={{ width: '5rem' }}
                body={(rowData: SkillTableItem) => {
                  const expanded = isRowExpanded(rowData);
                  const plainName = removeHighlight(rowData.name);
                  const actionLabel = expanded ? 'Collapse row' : 'Expand row';
                  return (
                    <button
                      type="button"
                      className="p-row-toggler p-link"
                      onClick={() => toggleRowExpansion(rowData)}
                      aria-label={`${actionLabel}: more details for skill ${plainName}`}
                      aria-expanded={expanded}
                      aria-controls={`skill-details-${rowData.id}`}
                    >
                      <span className={expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'} aria-hidden="true" />
                    </button>
                  );
                }}
              />
              <Column
                body={representativesItemTemplate}
                filterPlaceholder="Search"
                showFilterMenu={false}
                frozen
                header="Name"
                className="minWidth350"
              ></Column>
              <Column
                body={(e: SkillTableItem) => <p>{e.standards?.map(st => st.longName).join(', ') || []}</p>}
                filterPlaceholder="Search"
                showFilterMenu={false}
                frozen
                header="Standards"
                className="w-10"
              ></Column>
              {
                <Column
                  body={(item: SkillTableItem) => (
                    <div className="tw-flex tw-items-center tw-gap-4">
                      <PresentLessonButton
                        skill={{ id: item.id, code: removeHighlight(item.code), name: item.name }}
                        disabled={!item.lessons?.length}
                        showCoachmark={item.id === firstLessonSkillId}
                      />
                      <Button
                        className="border-none"
                        label="View"
                        outlined
                        onClick={() => navigate(`${skillUrl(item.code)}`)}
                      />
                      <AssignmentOpenModalButton
                        setCreateAssignmentModal={setCreateAssignmentModal}
                        entryPoint="skills"
                        skill={{ id: item.id, code: removeHighlight(item.code), name: removeHighlight(item.name) }}
                      />
                    </div>
                  )}
                  filterPlaceholder="Search"
                  showFilterMenu={false}
                  frozen
                  className="minWidth250"
                ></Column>
              }
            </DataTable>
          </div>
        )}
      </div>
      <Suspense>
        <QueryComponent
          setSkillList={setSkillList}
          setFetchingData={setFetchingData}
          setFirstLoad={setFirstLoad}
          setPageInfo={setPageInfo}
          setTotalRecords={setTotalRecords}
          pageInfo={pageInfo}
          filterState={filterState}
          fetchingData={fetchingData}
          firstLoad={firstLoad}
        />
      </Suspense>
    </>
  );
};

export default SkillsTable;
